import type { JSONContent } from '@tiptap/core';
import type {
  AccordionBlock,
  CalloutBlock,
  ColumnsBlock,
  CustomComponentBlock,
  DataDisplayBlock,
  EchartsBlock,
  FormulaCalloutBlock,
  GojsDiagramBlock,
  ImageBlock,
  MarkdownBlock,
  SimpleCalloutBlock,
  SimpleSolverBlock,
  SolverBlock,
  SuperLatexBlock,
  TableBlock,
  ToggleBlock,
  WidgetBlock,
} from '../model';
import { ContentBlock, ContentBlockType } from '../model';

export interface ConverterContext {
  popover?: boolean;
  transformImageSrc?: (src: string) => string;
}

export type BlockToTiptapConverter<T extends ContentBlock = ContentBlock> = (
  block: T,
  context?: ConverterContext,
) => JSONContent | JSONContent[];

let convertersCache: Record<string, BlockToTiptapConverter> | null = null;

function getConverters(): Record<string, BlockToTiptapConverter> {
  if (!convertersCache) {
    // Using require() for lazy loading to avoid circular dependency issues between converters
    /* eslint-disable @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports */
    const { convertMarkdownBlockToTiptap } = require('./block-converters/markdown-block.converter');
    const { convertCalloutBlockToTiptap } = require('./block-converters/callout-block.converter');
    const { convertTableBlockToTiptap } = require('./block-converters/table-block.converter');
    const { convertToggleBlockToTiptap } = require('./block-converters/toggle-block.converter');
    const { convertSuperLatexBlockToTiptap } = require('./block-converters/superlatex-block.converter');
    const { convertSolverBlockToTiptap } = require('./block-converters/solver-block.converter');
    const { convertSimpleSolverBlockToTiptap } = require('./block-converters/simple-solver-block.converter');
    const { convertWidgetBlockToTiptap } = require('./block-converters/widget-block.converter');
    const { convertImageBlockToTiptap } = require('./block-converters/image-block.converter');
    const { convertAccordionBlockToTiptap } = require('./block-converters/accordion-block.converter');
    const { convertSimpleCalloutBlockToTiptap } = require('./block-converters/simple-callout-block.converter');
    const { convertCustomComponentBlockToTiptap } = require('./block-converters/custom-component-block.converter');
    const { convertColumnsBlockToTiptap } = require('./block-converters/columns-block.converter');
    const { convertGojsDiagramBlockToTiptap } = require('./block-converters/gojs-diagram-block.converter');
    const { convertEchartsBlockToTiptap } = require('./block-converters/echarts-block.converter');
    const { convertDataDisplayBlockToTiptap } = require('./block-converters/data-display-block.converter');
    const { convertFormulaCalloutBlockToTiptap } = require('./block-converters/formula-callout-block.converter');
    /* eslint-enable @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports */

    convertersCache = {
      [ContentBlockType.Markdown]: (block, context) => convertMarkdownBlockToTiptap(block as MarkdownBlock, context),
      [ContentBlockType.Callout]: (block, context) => convertCalloutBlockToTiptap(block as CalloutBlock, context),
      [ContentBlockType.SimpleCallout]: (block, context) =>
        convertSimpleCalloutBlockToTiptap(block as SimpleCalloutBlock, context),
      [ContentBlockType.Table]: (block, context) => convertTableBlockToTiptap(block as TableBlock, context),
      [ContentBlockType.Toggle]: (block, context) => convertToggleBlockToTiptap(block as ToggleBlock, context),
      [ContentBlockType.SuperLatex]: (block) => convertSuperLatexBlockToTiptap(block as SuperLatexBlock),
      [ContentBlockType.Solver]: (block) => convertSolverBlockToTiptap(block as SolverBlock),
      [ContentBlockType.SimpleSolver]: (block) => convertSimpleSolverBlockToTiptap(block as SimpleSolverBlock),
      [ContentBlockType.Widget]: (block) => convertWidgetBlockToTiptap(block as WidgetBlock),
      [ContentBlockType.Image]: (block, context) => convertImageBlockToTiptap(block as ImageBlock, context),
      [ContentBlockType.Accordion]: (block, context) => convertAccordionBlockToTiptap(block as AccordionBlock, context),
      [ContentBlockType.CustomComponent]: (block) => convertCustomComponentBlockToTiptap(block as CustomComponentBlock),
      [ContentBlockType.Columns]: (block, context) => convertColumnsBlockToTiptap(block as ColumnsBlock, context),
      [ContentBlockType.GojsDiagram]: (block) => convertGojsDiagramBlockToTiptap(block as GojsDiagramBlock),
      [ContentBlockType.Echarts]: (block) => convertEchartsBlockToTiptap(block as EchartsBlock),
      [ContentBlockType.DataDisplay]: (block) => convertDataDisplayBlockToTiptap(block as DataDisplayBlock),
      [ContentBlockType.FormulaCallout]: (block, context) =>
        convertFormulaCalloutBlockToTiptap(block as FormulaCalloutBlock, context),
    };
  }
  return convertersCache;
}

/**
 * Converts an array of ContentBlocks to a Tiptap document JSON.
 */
export function contentBlocksToTiptap(blocks: ContentBlock[], context?: ConverterContext): JSONContent {
  return {
    type: 'doc',
    content: convertBlocksToTiptapNodes(blocks, context),
  };
}

/**
 * Converts an array of ContentBlocks to Tiptap JSON nodes.
 * This is the recursive helper used by block converters for nested content.
 *
 * Uses lazy-loaded converters to avoid circular dependency issues.
 */
export function convertBlocksToTiptapNodes(blocks: ContentBlock[], context?: ConverterContext): JSONContent[] {
  const converters = getConverters();
  const result: JSONContent[] = [];

  for (const block of blocks) {
    const converter = converters[block.type];
    if (!converter) {
      console.error(`No converter found for block type: ${block.type}`, block);
      continue;
    }

    try {
      const converted = converter(block, context);
      if (Array.isArray(converted)) {
        result.push(...converted);
      } else {
        result.push(converted);
      }
    } catch {
      console.error('Failed to convert block to Tiptap JSON: ', block);
    }
  }

  return result;
}
